import { Accordion, AccordionDetails, Chip, Typography } from '@fox/atoms';
import { QueryFilter } from '@fox/data-grid';
import { FlexColumnSpaced, FlexRowSmallSpaced, ResponsiveCSSGrid } from '@fox/layout';
import React from 'react';
import { ActionCentreColumn } from '../contracts';
import { SearchInput } from '../shared-components';
import { AddressFilter } from './address-filter';
import { ActionCentreFilterProps, FilterType } from './contracts';
import { DateRangeFilter } from './date-range-filter';
import { NumberRangeFilter } from './number-range-filter';
import { SelectFilter } from './select-filter';
import { StringFilter } from './string-filter';

const FilterMap: Record<FilterType, React.ComponentType<ActionCentreFilterProps>> = {
	date: DateRangeFilter,
	datetime: DateRangeFilter,
	select: SelectFilter,
	string: StringFilter,
	number: NumberRangeFilter,
	address: AddressFilter
} as const;

// Helper to render a filter for a column
const RenderFilter = ({
	column,
	filters,
	updateFilters,
	showAddressTypes,
	align
}: {
	column: ActionCentreColumn;
	updateFilters: (value: string | undefined, column: ActionCentreColumn) => void;
	filters: QueryFilter[];
	showAddressTypes: boolean;
	align: 'row' | 'column';
}) => {
	const Component: React.ComponentType<ActionCentreFilterProps> = FilterMap[column.filterType];
	if (!Component) return null;
	const filter = filters.find(f => f.dataIndex === column.id || f.dataIndex === column.title);
	return (
		<Component
			filter={filter}
			column={column}
			onChange={(value: string | undefined) => updateFilters(value, column)}
			customFieldMetadata={column.customFieldMetadata}
			align={align}
			showAddressTypes={showAddressTypes}
		/>
	);
};
export function RenderFilterList({
	updateFilters,
	columns,
	filters,
	includeSearch
}: {
	columns: ActionCentreColumn[];
	updateFilters: (value: string | undefined, column: ActionCentreColumn) => void;
	filters: QueryFilter[];
	includeSearch: boolean;
}) {
	const showAddressTypes = columns.filter(c => !!c.filterable && c.type === 'address').length > 1;
	return (
		<ResponsiveCSSGrid itemWidth="350px" gap="1.5rem">
			{includeSearch ? <SearchInput updateFilters={updateFilters} /> : null}
			{columns.map(column => RenderFilter({ column, filters, updateFilters, showAddressTypes, align: 'row' }))}
		</ResponsiveCSSGrid>
	);
}

export function RenderGroupedFilterList({
	updateFilters,
	columns,
	filters
}: {
	columns: ActionCentreColumn[];
	updateFilters: (value: string | undefined, column: ActionCentreColumn) => void;
	filters: QueryFilter[];
}) {
	const showAddressTypes = columns.filter(c => !!c.filterable && c.type === 'address').length > 1;

	// Group columns by filterGroup
	const grouped: Record<string, ActionCentreColumn[]> = {};
	const ungrouped: ActionCentreColumn[] = [];

	columns
		.filter(c => !!c.filterable)
		.forEach(col => {
			if (col.filterGroup) {
				if (!grouped[col.filterGroup]) grouped[col.filterGroup] = [];
				grouped[col.filterGroup].push(col);
			} else {
				ungrouped.push(col);
			}
		});

	return (
		<>
			{/* Render grouped filters in accordions */}
			{Object.entries(grouped).map(([groupName, groupColumns]) => {
				const numberOfFiltersInGroup = filters.filter(f =>
					groupColumns.find(g => g.id === f.dataIndex || g.title === f.dataIndex)
				).length;
				return (
					<Accordion
						key={groupName}
						title={
							<FlexRowSmallSpaced>
								<Typography variant="body1" component="h6">
									{groupName}
								</Typography>
								{numberOfFiltersInGroup > 0 ? <Chip size="small" label={numberOfFiltersInGroup} /> : null}
							</FlexRowSmallSpaced>
						}
					>
						<AccordionDetails style={{ paddingRight: 0 }}>
							<FlexColumnSpaced>
								{groupColumns.map(column =>
									RenderFilter({ column, filters, updateFilters, showAddressTypes, align: 'column' })
								)}
							</FlexColumnSpaced>
						</AccordionDetails>
					</Accordion>
				);
			})}
			{/* Render ungrouped filters as normal */}
			{ungrouped.map(column => RenderFilter({ column, filters, updateFilters, showAddressTypes, align: 'column' }))}
		</>
	);
}
